import React, { useState } from 'react';
import { useLocation, useNavigate, BrowserRouter as Router, Routes, Route } from "react-router-dom";
import "./App.css";

// Component Imports
import CNavbar from './Components/Navbar';
import PatientVisits from './Components/PatientVisits';
import ListofDoctors from './Components/ListofDoctors';
import EditPatientInfo from './Components/Edit/EditPatientInfo';
import EditPatientSymptoms from './Components/Edit/EditPatientSymptoms';
import EditPatientHistory from './Components/Edit/EditPatientHistory';
import EditPatientCI from './Components/Edit/EditPatientCI';
import EditPatientAnyavyadhi from './Components/Edit/EditPatientAnyavyadhi';
import EditPatientKulawrutta from './Components/Edit/EditPatientKulawrutta';
import EditPatientTapasyana from './Components/Edit/EditPatientTapasanya';
import EditPatientArtawawrutta from './Components/Edit/EditPatientArtawawrutta';
import EditPatientDiagnosis from './Components/Edit/EditPatientDiagnosis';
import EditPatientTreatment from './Components/Edit/EditPatientTreatment';
import EditPatientNextVisit from './Components/Edit/EditPatientNextVisit';
import EditPatientPaymentForm from './Components/Edit/EditPatientPaymentForm';
import AddPatientInfo from './Components/Add/AddPatientInfo';
import AddClinic from './Components/Add/AddClinic';
import AddDoctor from './Components/Add/AddDoctor';
import EditMandaliInfo from './Components/Edit/EditMandaliInfo';
import CheckForm from './Components/Mandali/CheckForm';
import SelectPatientAndCases from './Components/SelectPatientAndCases';
import AuthProvider from './context/AuthProvider';
import MyProfileInfo from './Components/MyProfileInfo';
import { PatientProvider } from './context/PatientContext';
import AddNewCase from './Components/Add/AddNewCase';
import SelectedPatientDetails from './Components/SelectedPatientDetails';
import PatientLayout from "./Components/PatientLayout";
import MyCalendarView from './Components/MyCalendarView';
import AddAttendant from './Components/Add/AddAttendant';
import PatientPaymentReport from './Components/Reports/PatientPaymentReport';
import { ReactTransliterate } from "react-transliterate";
import LandingPage from './Components/LandingPage';
import "react-transliterate/dist/index.css";
import { ThemeProvider } from './context/ThemeContext';
import ContactUs from './Components/ContactUs';

// AppContent component handles navbar visibility globally
const AppContent = () => {
  const location = useLocation();
  const showNavbar = location.pathname !== '/' && location.pathname !== '/login' && location.pathname !== '/contact-us';

  return (
    <div className="App">
      {showNavbar && <CNavbar />}
      <Routes>
        {/* Landing page - no navbar */}
        <Route path="/" element={<LandingPage />} />
        <Route path="/contact-us" element={<ContactUs />} />
        <Route path="/login" element={<CheckForm />} />

        {/* Protected routes - with navbar */}
        {/* <Route path="/addpatient" element={<AddPatientInfo />} /> */}
        <Route path="/getmandaliinfo" element={<MyProfileInfo />} />
        <Route path="/edit-doctor/:id" element={<EditMandaliInfo />} />
        <Route path="/doctorslist" element={<ListofDoctors />} />
        <Route path="/add-doctor" element={<AddDoctor />} />
        <Route path="/add-Attendant" element={<AddAttendant />} />
        <Route path="/add-clinic" element={<AddClinic />} />
        <Route path="/calendar" element={<MyCalendarView />} />
        <Route path="/patientlist" element={<SelectPatientAndCases />} />
        <Route path="/reports/payments" element={<PatientPaymentReport />} />
        <Route path="/patient/:patientId" element={<SelectedPatientDetails />} />
        <Route path="/visits/:patientId/:caseId" element={<PatientVisits />} />
        <Route path="/visits/:patientId/:caseId/addcase" element={<AddNewCase />} />

        <Route path="/addpatient" element={<PatientLayout />}>
          <Route index element={<AddPatientInfo />} />
          <Route path="symptoms" element={<EditPatientSymptoms />} />
          <Route path="history" element={<EditPatientHistory />} />
          <Route path="ci" element={<EditPatientCI />} />
          <Route path="anyavyadhi" element={<EditPatientAnyavyadhi />} />
          <Route path="kulawrutta" element={<EditPatientKulawrutta />} />
          <Route path="tapasanya" element={<EditPatientTapasyana />} />
          <Route path="artawawrutta" element={<EditPatientArtawawrutta />} />
          <Route path="diagnosis" element={<EditPatientDiagnosis />} />
          <Route path="treatment" element={<EditPatientTreatment />} />
          <Route path="payment" element={<EditPatientPaymentForm />} />
          <Route path="nextvisit" element={<EditPatientNextVisit />} />
        </Route>
        {/* 👇 Dynamic layout route for visit-related forms */}
        <Route path="/visits/:patientId/:caseId/:visitId/" element={<PatientLayout />}>
          <Route index element={<EditPatientInfo />} /> {/* Default tab */}
          <Route path="symptoms" element={<EditPatientSymptoms />} />
          <Route path="history" element={<EditPatientHistory />} />
          <Route path="ci" element={<EditPatientCI />} />
          <Route path="anyavyadhi" element={<EditPatientAnyavyadhi />} />
          <Route path="kulawrutta" element={<EditPatientKulawrutta />} />
          <Route path="tapasanya" element={<EditPatientTapasyana />} />
          <Route path="artawawrutta" element={<EditPatientArtawawrutta />} />
          <Route path="diagnosis" element={<EditPatientDiagnosis />} />
          <Route path="treatment" element={<EditPatientTreatment />} />
          <Route path="payment" element={<EditPatientPaymentForm />} />
          <Route path="nextvisit" element={<EditPatientNextVisit />} />
        </Route>
      </Routes>
    </div>
  );
};

// This inner component allows us to use useLocation correctly
const App = () => {
  return (
    <Router>
      <ThemeProvider>
        <PatientProvider>
          <AuthProvider>
            <AppContent />
          </AuthProvider>
        </PatientProvider>
      </ThemeProvider>
    </Router>
  );
};

// Helper Components
const PatientSearchBox = ({ onSearch }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const clientSearch = () => {
    onSearch(searchTerm);
  };

  return (
    <div>
      <ReactTransliterate
        value={searchTerm}
        onChangeText={(text) => setSearchTerm(text)}
        lang="hi"
        placeholder="Search patients by name (Type in English for Devanagari)"
        renderComponent={(props) => <input {...props} />}
      />
      <button onClick={clientSearch}>Search</button>
    </div>
  );
};

const PatientCaseSelect = ({ patientId, caseId }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const handleCaseSelect = (pId, cId) => {
    navigate(`/visits/${pId}/${cId}`, { state: location.state });
  };

  return (
    <div>
      <button onClick={() => handleCaseSelect(patientId, caseId)}>View Visits</button>
    </div>
  );
};

export default App;